import React, { useMemo, useState, useEffect } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { FileText, CheckCircle2, AlertTriangle, BookOpen, RefreshCw, Trash2, ChevronDown } from "lucide-react";
import { useApiData, apiFetch } from "../../lib/api.js";
import { getCurrentUser } from "../../lib/auth.js";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";

const ZAKLAD_KAT = "Základ ke spolupráci";

// === TRENER DOKUMENTY — list firemních dokumentů s per-uživatelem statusem ===
// Status badge:
//   signed   = ✓ přečteno (verze potvrzena = aktuální)
//   outdated = ⚠ aktualizováno (potvrdil staršší verzi, musí znovu)
//   unread   = 🆕 nepřečteno (žádný sign-off pro tento dokument)
// Klik na kartu → /trener/dokumenty/:slug (interaktivní reader)
export default function TrenerDokumenty({ bezHlavicky = false }) {
  const { activeTrener } = useOutletContext() || {};
  const user = getCurrentUser();
  const email = user?.email || "";
  // pilot = hraný trenér (owner impersonace). Backend podle něj dohledá správný účet
  // a stav přečtení počítá za toho trenéra, ne za přihlášeného ownera.
  const pilotQ = activeTrener ? `&pilot=${encodeURIComponent(activeTrener)}` : "";
  const { data, loading, error, refetch, refreshing } = useApiData(`/api/dokumenty?email=${encodeURIComponent(email)}${pilotQ}`);
  const [filter, setFilter] = useState("vse"); // vse | nepřečtené | aktualizované | přečtené
  const [katFilter, setKatFilter] = useState("vse");
  const [confirmDoc, setConfirmDoc] = useState(null); // dokument ke smazání (designový confirm)
  const [deleteErr, setDeleteErr] = useState("");
  const canManage = ["owner", "manager"].includes(String(user?.role || "").toLowerCase());

  const handleDelete = (doc) => { setDeleteErr(""); setConfirmDoc(doc); };
  const handleDeleteConfirmed = async (doc) => {
    try {
      const r = await apiFetch("/api/dokumenty", { method: "POST", body: JSON.stringify({ action: "delete", slug: doc.slug }) });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.error || "Smazání selhalo"); }
      refetch();
    } catch (e) { setDeleteErr(e.message || "Smazání selhalo"); }
  };

  const docs = data?.dokumenty || [];

  // Tři sekce: Základ ke spolupráci (manuály + mise) / K prostudování (unread+outdated)
  // / Hotovo (signed). Základ má přednost před read-statusem — je to referenční knihovna.
  const sections = useMemo(() => {
    const zaklad = [], kProstudovani = [], hotovo = [];
    for (const d of docs) {
      if (d.kategorie === ZAKLAD_KAT) zaklad.push(d);
      else if (d.status === "signed") hotovo.push(d);
      else kProstudovani.push(d);
    }
    const byName = (a, b) => a.nazev.localeCompare(b.nazev, "cs");
    return [
      { key: "kprostudovani", label: "K prostudování", color: "amber", items: kProstudovani.sort((a, b) => ({ unread: 0, outdated: 1 }[a.status] - { unread: 0, outdated: 1 }[b.status]) || byName(a, b)), defaultOpen: true },
      { key: "zaklad", label: "Základ ke spolupráci", color: "violet", items: zaklad.sort(byName), defaultOpen: true },
      { key: "hotovo", label: "Hotovo", color: "emerald", items: hotovo.sort(byName), defaultOpen: false },
    ];
  }, [docs]);

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Hlavička stránky, jednotná s celým portálem (fáze 3, 3.9.2026); ve Školení trenéra ji nahrazuje hlavička Školení */}
      {!bezHlavicky && <div className="rounded-2xl bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 p-4 text-white shadow-glass sm:p-5">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 sm:h-11 sm:w-11">
            <BookOpen className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-serif text-xl font-bold leading-tight sm:text-2xl">Dokumenty</div>
            <p className="mt-1 text-xs leading-snug text-white/75 sm:text-sm">Manuály, procesy a návody</p>
          </div>
          <button
            onClick={refetch}
            disabled={refreshing}
            aria-label="Obnovit"
            title="Obnovit"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white transition active:scale-95 active:bg-white/20 disabled:opacity-40 sm:h-10 sm:w-10 sm:hover:bg-white/20"
          >
            <RefreshCw className={`h-[18px] w-[18px] ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>}

      {/* Stav načítání */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
          Chyba: {error}
        </div>
      )}
      {loading && (
        <div className="flex items-center justify-center py-10 text-sm text-slate-400">
          <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Načítám…
        </div>
      )}

      {!loading && docs.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl py-10 px-4 text-center">
          <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <div className="text-sm text-slate-500">Zatím tu nejsou žádné dokumenty.</div>
        </div>
      )}

      {/* Sekce */}
      {!loading && sections.map((sec) => (
        <DocSection key={sec.key} section={sec} canManage={canManage} onDelete={handleDelete} />
      ))}

      {deleteErr && (
        <div className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">Chyba: {deleteErr}</div>
      )}
      <ConfirmDialog
        open={!!confirmDoc}
        title="Smazat dokument?"
        description={<>Dokument <strong>{confirmDoc?.nazev}</strong> se smaže. Podpisy zůstanou v historii.</>}
        confirmLabel="Smazat"
        variant="danger"
        onConfirm={() => { const d = confirmDoc; setConfirmDoc(null); handleDeleteConfirmed(d); }}
        onCancel={() => setConfirmDoc(null)}
      />
    </div>
  );
}

// Rozbalovací sekce dokumentů (K prostudování / Základ / Hotovo).
function DocSection({ section, canManage, onDelete }) {
  const [open, setOpen] = useState(section.defaultOpen);
  const colorMap = {
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    violet: "bg-brand-50 border-brand-200 text-brand-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  };
  if (section.items.length === 0) return null;
  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center gap-2.5 px-4 py-3 rounded-xl border font-bold transition-colors active:brightness-95 ${colorMap[section.color]}`}
      >
        <ChevronDown className={`w-5 h-5 transition-transform ${open ? "" : "-rotate-90"}`} />
        <span className="text-base">{section.label}</span>
        <span className="text-sm font-bold tabular-nums px-2 py-0.5 rounded-full bg-white/70 border border-black/5 ml-auto">{section.items.length}</span>
      </button>
      {open && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {section.items.map((d) => <DocCard key={d.slug} doc={d} canManage={canManage} onDelete={onDelete} />)}
        </div>
      )}
    </div>
  );
}

function DocCard({ doc, canManage, onDelete }) {
  const status = doc.status || "unread";
  const styles = {
    unread:   { badge: "bg-amber-100 text-amber-700 border-amber-200", icon: AlertTriangle, label: "Nepřečteno", ring: "border-amber-200 hover:border-amber-400" },
    outdated: { badge: "bg-orange-100 text-orange-700 border-orange-200", icon: AlertTriangle, label: `Aktualizováno (verze ${doc.version})`, ring: "border-orange-200 hover:border-orange-400" },
    signed:   { badge: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2, label: `Přečteno (verze ${doc.signedVersion})`, ring: "border-emerald-200 hover:border-emerald-400" },
  };
  const s = styles[status] || styles.unread;
  const Icon = s.icon;

  return (
    <Link
      to={`/trener/dokumenty/${encodeURIComponent(doc.slug)}`}
      className={`block bg-white rounded-xl border-2 ${s.ring} p-4 shadow-sm hover:shadow-md transition-all`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-slate-600">
            {doc.kategorie || "Ostatní"}
          </span>
          <div className="mt-1.5 text-sm font-bold leading-snug text-slate-900">{doc.nazev}</div>
        </div>
        <div className={`text-xs px-2 py-1 rounded-full border font-semibold whitespace-nowrap inline-flex items-center gap-1 ${s.badge}`}>
          <Icon className="w-3 h-3" />
          {s.label}
        </div>
      </div>
      {doc.popis && (
        <div className="text-xs text-slate-600 line-clamp-2 mb-2">{doc.popis}</div>
      )}
      <div className="flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-2">
        <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold tabular-nums text-slate-600">
          {doc.updated_at || doc.created_at}
        </span>
        <div className="ml-auto inline-flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold tabular-nums text-slate-600">Verze {doc.version}</span>
          {canManage && (
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(doc); }}
              title="Smazat dokument"
              className="text-slate-300 hover:text-rose-600 p-1 -mr-1 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}
